import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { get, post } from '/api.js';

/**
 * Turning chat notifications on for this phone.
 *
 * Three things have to be true and each fails differently, so each is said
 * differently rather than collapsed into one dead switch:
 *
 *  - the server has push configured at all (no contact address, no keys)
 *  - the browser supports it (a desktop Safari, or an iPhone where the app has
 *    not been added to the home screen, simply cannot)
 *  - the person has allowed it, which is a permission prompt that can be
 *    refused permanently and cannot be asked again from script
 *
 * The subscription belongs to THIS browser, which is why the switch reads from
 * the browser rather than from the server: another phone being subscribed is
 * not this one being subscribed, and a switch that says On while this device
 * stays silent is worse than no switch.
 */
export function Notifications() {
  const [key, setKey] = useState(undefined);   // undefined = still asking
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const can = 'serviceWorker' in navigator && 'PushManager' in window
              && 'Notification' in window;

  useEffect(() => {
    get('/api/push/key').then((r) => setKey(r.key)).catch(() => setKey(null));
  }, []);

  useEffect(() => {
    if (!can) { setOn(false); return; }
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setOn(Boolean(sub)))
      .catch(() => setOn(false));
  }, [can]);

  if (key === undefined) return null;
  if (key === null) {
    return html`
      <div class="stack" data-testid="notify">
        <label>Notifications</label>
        <p class="sub" data-testid="notify-off-server">
          Not set up on this server.
        </p>
      </div>`;
  }
  if (!can) {
    return html`
      <div class="stack" data-testid="notify">
        <label>Notifications</label>
        <p class="sub" data-testid="notify-unsupported">
          This browser cannot show them. On an iPhone, add Cueforge to the
          home screen first.
        </p>
      </div>`;
  }

  const turnOn = async () => {
    setBusy(true); setError('');
    try {
      const allowed = await Notification.requestPermission();
      if (allowed !== 'granted') {
        // Denial is sticky: script cannot ask again, so say where it lives.
        setError('Blocked. Allow notifications for this site in your '
                 + "browser's settings, then try again.");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: bytes(key),
      });
      await post('/api/push/subscribe', { subscription: sub.toJSON() });
      setOn(true);
    } catch (e) {
      setError(e.detail || e.message || 'could not turn them on');
    } finally { setBusy(false); }
  };

  const turnOff = async () => {
    setBusy(true); setError('');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await post('/api/push/unsubscribe', { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setOn(false);
    } catch (e) {
      setError(e.detail || e.message || 'could not turn them off');
    } finally { setBusy(false); }
  };

  return html`
    <div class="stack" data-testid="notify">
      <label>Notifications</label>
      <div class="row pair">
        <p class="sub" style="margin:0" data-testid="notify-state">
          ${on ? 'On for this phone' : 'Off'}
        </p>
        <button class=${on ? 'secondary' : ''} disabled=${busy || on === null}
                data-testid="notify-toggle"
                onClick=${on ? turnOff : turnOn}>
          ${on ? 'Turn off' : 'Turn on'}
        </button>
      </div>
      <p class="sub">
        A message in one of your circles will show on this phone. Nothing else
        does — a match starting is something you are already at.
      </p>
      ${error && html`<p class="error" data-testid="notify-error">${error}</p>`}
    </div>`;
}

/** base64url to the Uint8Array PushManager wants. */
function bytes(key) {
  const pad = '='.repeat((4 - (key.length % 4)) % 4);
  const raw = atob((key + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}
